import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/utils";
import { buttonVariants } from "@/components/ui/button-variants";
import { IconColor } from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

export type ButtonSize = "default" | "lg" | "icon";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "secondaryNoBg"
  | "tertiary"
  | "icon"
  | "iconWShadow"
  | "iconNoBg"
  | "link"
  | "filter";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltipText?: string;
  destructive?: boolean;
  selected?: boolean;
  iconColor?: IconColor;
  open?: boolean;
  iconClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      destructive,
      selected,
      disabled,
      asChild = false,
      open,
      tooltipText,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const ButtonComponent = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled}
        ref={ref}
        {...props}
        data-destructive={destructive}
        data-selected={selected}
        data-size={size || "default"}
        data-disabled={disabled ? "true" : "false"}
      >
        {children}
      </Comp>
    );
    return (
      <>
        {tooltipText ? (
          <Tooltip open={open}>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex size-fit">
                {ButtonComponent}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-56">{tooltipText}</TooltipContent>
          </Tooltip>
        ) : (
          ButtonComponent
        )}
      </>
    );
  }
);
Button.displayName = "Button";

export { Button };
